import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { 
  LayoutDashboard, 
  Package, 
  Upload, 
  BarChart3, 
  Settings, 
  Users, 
  Database, 
  LogOut,
  Menu,
  X,
  Building2,
  FileSpreadsheet,
  AlertTriangle,
  Warehouse,
  Scale,
  ShoppingCart,
  ClipboardList,
  PackageCheck,
  ArrowLeftRight,
  Tag,
  Factory,
  PanelLeftClose,
  PanelLeftOpen
} from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on mobile/tablet (< 1024px)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [userCollapsed, setUserCollapsed] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        // Only auto-open on desktop if user hasn't manually collapsed
        if (!userCollapsed) setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [userCollapsed]);

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const adminNavItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/inventory-hq', icon: Warehouse, label: 'Inventory HQ' },
    { to: '/transfer-recommendations', icon: ArrowLeftRight, label: 'Transfer Recs' },
    { to: '/orders', icon: ShoppingCart, label: 'Reorders' },
    { to: '/delivered-orders', icon: PackageCheck, label: 'Delivered Orders' },
    { to: '/bulk-orders', icon: ClipboardList, label: 'Bulk Orders' },
    { to: '/production-program', icon: Factory, label: 'Production Program' },
    { to: '/analytics', icon: BarChart3, label: 'Sales Analytics' },
    { to: '/buffer-review', icon: Scale, label: 'Buffer Review' },
    { to: '/item-masters', icon: Tag, label: 'Item Masters' },
    { to: '/upload', icon: Upload, label: 'Upload Data' },
    { to: '/master-data', icon: Database, label: 'Master Data' },
    { to: '/depots', icon: Building2, label: 'Depot Management' },
    { to: '/users', icon: Users, label: 'User Management' },
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  const factoryAdminNavItems = [
    { to: '/depot-dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/inventory-hq', icon: Warehouse, label: 'Inventory HQ' },
    { to: '/bulk-orders', icon: ClipboardList, label: 'Depot Orders' },
    { to: '/orders', icon: ShoppingCart, label: 'Reorders' },
    { to: '/delivered-orders', icon: PackageCheck, label: 'Delivered Orders' },
    { to: '/production-program', icon: Factory, label: 'Production Program' },
    { to: '/analytics', icon: BarChart3, label: 'Sales Analytics' },
  ];

  // Check if user is Jaipur depot (special access to Transfer Recommendations)
  const isJaipurDepot = user?.role === 'depot' && 
    user?.depot_name?.toLowerCase().includes('jaipur');

  // Build depot nav items dynamically - add Transfer Recs only for Jaipur
  const getDepotNavItems = () => {
    const items = [
      { to: '/depot-dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/inventory', icon: Package, label: 'Inventory' },
      { to: '/inventory-hq', icon: Warehouse, label: 'Inventory HQ' },
    ];
    
    // Add Transfer Recommendations only for Jaipur depot
    if (isJaipurDepot) {
      items.push({ to: '/transfer-recommendations', icon: ArrowLeftRight, label: 'Transfer Recs' });
    }
    
    items.push(
      { to: '/bulk-orders', icon: ClipboardList, label: 'My Orders' },
      { to: '/orders', icon: ShoppingCart, label: 'All Reorders' },
      { to: '/delivered-orders', icon: PackageCheck, label: 'Delivered Orders' },
      { to: '/analytics', icon: BarChart3, label: 'Sales Analytics' },
      { to: '/item-masters', icon: Tag, label: 'Item Masters' },
      { to: '/upload', icon: Upload, label: 'Upload Data' }
    );
    
    return items;
  };

  const isFactoryAdmin = user?.role === 'factory_admin';
  const navItems = isAdmin ? adminNavItems : (isFactoryAdmin ? factoryAdminNavItems : getDepotNavItems());

  const toggleSidebar = () => {
    const next = !sidebarOpen;
    setSidebarOpen(next);
    // Track desktop collapse preference
    if (!isMobile) setUserCollapsed(!next);
  };

  return (
    <div className="app-container flex">
      {/* Mobile overlay backdrop */}
      {isMobile && sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-overlay"
        />
      )}

      {/* Mobile menu button */}
      <button 
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-slate-200"
        onClick={toggleSidebar}
        data-testid="mobile-menu-toggle"
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
      >
        {sidebarOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
      </button>

      {/* Desktop expand button (visible only when sidebar is collapsed) */}
      {!isMobile && !sidebarOpen && (
        <button
          className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-slate-200 hover:bg-slate-50 transition-colors"
          onClick={toggleSidebar}
          data-testid="desktop-sidebar-expand"
          aria-label="Open sidebar"
        >
          <PanelLeftOpen className="w-5 h-5 text-slate-700" />
        </button>
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isMobile ? (sidebarOpen ? 'mobile-open' : '') : (sidebarOpen ? '' : 'sidebar-collapsed')}`}>
        <div className="flex flex-col h-full">
          {/* Logo + Collapse toggle */}
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Stock Reorder Pro
                  </h1>
                  <p className="text-xs text-slate-500">Inventory System</p>
                </div>
              </div>
              {!isMobile && (
                <button
                  className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                  onClick={toggleSidebar}
                  data-testid="desktop-sidebar-collapse"
                  aria-label="Collapse sidebar"
                >
                  <PanelLeftClose className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <Separator />

          {/* User Info */}
          <div className="p-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="font-medium text-sm text-slate-900">{user?.name}</p>
              <p className="text-xs text-slate-500">
                {user?.role === 'admin' ? 'Administrator' : 
                 user?.role === 'factory_admin' ? 'Factory Admin' : 
                 user?.depot_name || 'Depot User'}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex-1 px-3 overflow-y-auto sidebar-nav">
            <nav className="space-y-1 py-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>

          {/* Logout */}
          <div className="p-4">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${isMobile ? '' : (sidebarOpen ? '' : 'main-content-expanded')} flex-1`}>
        <div className={`max-w-[1600px] mx-auto px-4 sm:px-6 py-6 sm:py-8 ${isMobile || (!isMobile && !sidebarOpen) ? 'pt-16' : 'pt-8'}`}>
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
